import React, { useState } from 'react';

const Levey052025: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [error, setError] = useState('');

  const correctPassword = 'levey2025'; // WARNING: Password stored in frontend code - not secure!

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (passwordInput === correctPassword) {
      setIsAuthenticated(true);
      setError('');
    } else {
      setError('Incorrect password');
      setPasswordInput('');
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-sm">
          <h2 className="text-2xl font-bold mb-6 text-center">Enter Password</h2>
          <form onSubmit={handlePasswordSubmit}>
            <div className="mb-4">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">Password:</label>
              <input
                type="password"
                id="password"
                value={passwordInput}
                onChange={(e) => setPasswordInput(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                required
              />
            </div>
            {error && <p className="text-red-500 text-sm mb-4">{error}</p>}
            <button
              type="submit"
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition duration-200"
            >
              Submit
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-4xl mx-auto font-sans">
      {/* Header */}
      <div className="mb-10 text-center border-b border-indigo-100 pb-6">
        <h1 className="text-3xl font-serif font-medium text-indigo-950 mb-2">Proposal for Strategic Advisory on Growth, Branding, and Positioning</h1>
        <p className="text-lg text-gray-600 font-light mb-1">Prepared by Anant Vinjamoori, MD MBA</p>
        <p className="text-md text-gray-500 font-light">May 2025</p>
      </div>

      {/* Overview Section */}
      <div className="mb-8">
        <h2 className="text-2xl font-serif font-medium text-indigo-950 mb-4">Overview</h2>
        <p className="text-gray-800 mb-4 text-sm leading-relaxed">
          Thanks again for the conversation. Based on what you shared, you're at a stage where the core clinical model is in place and working, and the next priority is clear: driving focused growth while tightening brand positioning and program structure.
        </p>
        <p className="text-gray-800 mb-4 text-sm leading-relaxed">
          You’re already attracting referrals and seeing meaningful interest. The challenge now is reaching more of the right people—those actively searching for a high-quality longevity clinic—and giving them a clear, compelling reason to choose you.
        </p>
        <p className="text-gray-800 mb-6 text-sm leading-relaxed">
          This engagement is designed to support exactly that. We’ll focus on refining your messaging, packaging your offering for better conversion, and identifying the highest-impact acquisition channels. The structure is lightweight, practical, and designed for early-stage teams who want to move fast and stay focused.
        </p>
      </div>

      {/* Engagement Scope Section */}
      <div className="mb-10">
        <h2 className="text-2xl font-serif font-medium text-indigo-950 mb-4">Engagement Scope</h2>
        
        <p className="text-gray-800 mb-4 text-sm leading-relaxed">
          We’ll work together across three key areas:
        </p>

        <div className="space-y-6 mb-8">
          {/* Sub-section 1 */}
          <div className="bg-indigo-50 border-l-4 border-indigo-900 p-6">
            <h3 className="text-lg font-medium text-indigo-950 mb-3">1. Brand Positioning & Messaging</h3>
            <ul className="space-y-2 text-sm text-gray-800">
              <li className="flex items-start">
                <span className="text-indigo-700 mr-2">•</span>
                <span>Refine core brand language and narrative to better communicate your point of view</span>
              </li>
              <li className="flex items-start">
                <span className="text-indigo-700 mr-2">•</span>
                <span>Align content and site copy with a more specific, differentiating message</span>
              </li>
              <li className="flex items-start">
                <span className="text-indigo-700 mr-2">•</span>
                <span>Advise on how founder-led vs. clinician-led storytelling can support trust and growth</span>
              </li>
            </ul>
          </div>
          
          {/* Sub-section 2 */}
          <div className="bg-indigo-50 border-l-4 border-indigo-900 p-6">
            <h3 className="text-lg font-medium text-indigo-950 mb-3">2. Program Design & Packaging</h3>
            <ul className="space-y-2 text-sm text-gray-800">
              <li className="flex items-start">
                <span className="text-indigo-700 mr-2">•</span>
                <span>Reframe the way offerings are presented to better match how patients search and make decisions</span>
              </li>
              <li className="flex items-start">
                <span className="text-indigo-700 mr-2">•</span>
                <span>Explore opportunities to merchandise the program without diluting the integrated care model</span>
              </li>
              <li className="flex items-start">
                <span className="text-indigo-700 mr-2">•</span>
                <span>Advise on tiering, enterprise structuring, or product “entry points” if applicable</span>
              </li>
            </ul>
          </div>
          
          {/* Sub-section 3 */}
          <div className="bg-indigo-50 border-l-4 border-indigo-900 p-6">
            <h3 className="text-lg font-medium text-indigo-950 mb-3">3. Growth Strategy</h3>
            <ul className="space-y-2 text-sm text-gray-800">
              <li className="flex items-start">
                <span className="text-indigo-700 mr-2">•</span>
                <span>Analyze available visit-level or acquisition data for audience and conversion insights</span>
              </li>
              <li className="flex items-start">
                <span className="text-indigo-700 mr-2">•</span>
                <span>Optimize content direction for high-intent search channels (e.g., SEO, SEM)</span>
              </li>
              <li className="flex items-start">
                <span className="text-indigo-700 mr-2">•</span>
                <span>Explore additional growth paths (e.g., employer partnerships, wealth manager networks, gym collaborations)</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Engagement Model Section */}
      <div className="mb-10">
        <h2 className="text-2xl font-serif font-medium text-indigo-950 mb-4">Engagement Model: Monthly Retainer</h2>
        
        <div className="bg-indigo-900 text-white rounded-sm p-6 mb-6">
          <p className="text-xl font-medium mb-1">Fee: $2,500/month</p>
        </div>

        <p className="text-gray-800 mb-2 text-sm font-medium">Structure:</p>
        <ul className="space-y-2 pl-4 mb-6 text-sm text-gray-800">
          <li className="flex items-start">
            <span className="text-indigo-700 mr-2">•</span>
            <span>Shared Slack channel for async collaboration</span>
          </li>
          <li className="flex items-start">
            <span className="text-indigo-700 mr-2">•</span>
            <span>Occasional Zoom syncs as needed (1–2x/month)</span>
          </li>
          <li className="flex items-start">
            <span className="text-indigo-700 mr-2">•</span>
            <span>I will share content frameworks, dashboards, and analysis developed from working with clinics across the country- including some of the best in the field</span>
          </li>
          <li className="flex items-start">
            <span className="text-indigo-700 mr-2">•</span>
            <span>Input on brand copy, landing pages, content direction, and program design</span>
          </li>
        </ul>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="border border-gray-200 p-5 rounded-sm">
            <p className="text-indigo-950 font-medium mb-1 text-sm">Duration</p>
            <p className="text-gray-800 text-sm">Month-to-month, with 30-day notice for cancellation or adjustment</p>
          </div>
          
          <div className="border border-gray-200 p-5 rounded-sm">
            <p className="text-indigo-950 font-medium mb-1 text-sm">Start Date</p>
            <p className="text-gray-800 text-sm">Flexible; ready to begin immediately</p>
          </div>
        </div>
      </div>

      {/* Why This Works Section */}
      <div className="mb-10">
        <h2 className="text-2xl font-serif font-medium text-indigo-950 mb-4">Why This Works</h2>
        <p className="text-gray-800 mb-4 text-sm leading-relaxed">
          I’ve advised clinics, startups, and health optimization ventures at every stage—from first cohort through national scale. My experience spans product, clinical strategy, and brand positioning. I bring focused insight, pattern recognition, and speed. No fluff—just strategic input to help you move forward with clarity.
        </p>
      </div>

      {/* Confidentiality & Collaboration Section */}
      <div className="mb-10">
        <h2 className="text-2xl font-serif font-medium text-indigo-950 mb-4">Confidentiality & Collaboration</h2>
        <p className="text-gray-800 mb-4 text-sm leading-relaxed">
          All materials shared will be treated as strictly confidential. While I advise other companies in the longevity and health optimization space, I do not cross-pollinate materials or insights. Each engagement is handled discretely and independently.
        </p>
      </div>

      {/* Next Steps Section */}
      <div className="mb-8 border-t border-gray-200 pt-8">
        <h2 className="text-2xl font-serif font-medium text-indigo-950 mb-4">Next Steps</h2>
        <p className="text-gray-800 mb-4 text-sm leading-relaxed">
          If this approach makes sense, I can open a Slack channel and begin reviewing your current materials this week. We’ll prioritize what’s most useful and keep momentum focused.
        </p>
        <p className="text-gray-800 mb-4 text-sm leading-relaxed">
          Looking forward to hearing your thoughts.
        </p>
      </div>

      {/* Footer / Signature */}
      <div className="mt-12 pt-6 border-t border-gray-200">
        <p className="text-gray-800 mb-4 text-sm leading-relaxed">Best,</p>
        <p className="text-indigo-950 font-medium">Anant Vinjamoori, MD MBA</p>
        <p className="text-gray-800 text-sm">Founder, Next Generation Medicine</p>
                      <a href="mailto:anant@nextgenerationmedicine.co" className="text-indigo-700 hover:text-indigo-900 text-sm">
                anant@nextgenerationmedicine.co
        </a>
      </div>
    </div>
  );
};

export default Levey052025;
